// The Radar's data, from the snapshot or (?live=1) her league's Stats file (a merged Radar's live
// data is at the end; its snapshot data stays in RadarComparison). Single-player: two
// steps, because the template sits between them: step 1 gives the season and her default
// template; the module then settles the template + axes; step 2 gives her values and each spoke's
// scale for those axes. Each step is picked once, at load (`LIVE`), as Ranking's board is — the
// snapshot site runs exactly the hooks it always did.

import { useMemo } from "react";
import type { ProtoRadarData } from "@catbento/shared";
import { readSnapshot, type RawPlayerRecord } from "../data/snapshot.js";
import type { PlayerId } from "../data/players.js";
import type { RadarAxis, RadarRange } from "../modules/radar-view.js";
import { LIVE } from "../live/flag.js";
import { herTemplate, herValuesAt, liveRadarRanges, radarComparison, templatePopulation } from "../live/radar.js";
import { rememberWomens } from "../live/player-gender.js";
import { useLiveGroupFile, useLiveGroupFiles, type LiveGroupFile } from "./useGroupFile.js";
import { useRadarData } from "./useRadarData.js";
import { useRadarRanges } from "./useRadarRanges.js";

/** Step 1 — the player's side: which season, and the template a fresh radar opens on. */
export interface RadarSeason {
  /** The season picker's list, newest first (live: her league's built seasons). */
  seasonCodes: string[];
  seasonCode: string;
  /** Her position (live: most minutes in this file); null = none known. */
  defaultTemplate: string | null;
  /** Live: no file for the season shown → why ("Premier League 22/23 isn't built yet"). */
  noFile: string | null;
  src: { kind: "snapshot"; data: ProtoRadarData } | { kind: "live"; group: LiveGroupFile; playerId: PlayerId };
}

/** Step 2 — her values on the axes, and each spoke's scale. */
export interface RadarValues {
  values: Record<string, number | null>;
  /** False → "No radar data captured for …" (live: she has no minutes at the template). */
  hasData: boolean;
  ranges: Map<string, RadarRange>;
  /** Who the scale is made from — the Distribution view's curves. */
  population: readonly RawPlayerRecord[];
}

function useSnapshotRadarSeason(name: string, _id: PlayerId | null, season: string | undefined): RadarSeason | null {
  const data = useRadarData(name, season);
  if (!data) return null;
  return {
    seasonCodes: data.seasonCodes,
    seasonCode: data.seasonCode,
    defaultTemplate: data.positionTemplate,
    noFile: null,
    src: { kind: "snapshot", data },
  };
}

function useLiveRadarSeason(_name: string, id: PlayerId | null, season: string | undefined): RadarSeason | null {
  // Live always has one: the module falls back to the snapshot's first player (a real DB id).
  if (id === null) throw new Error("A live Radar needs a player");
  const group = useLiveGroupFile(id, season ?? null);
  // The picker lists the league's built seasons; the season shown is added if it isn't built,
  // so the picker never shows a blank (as Ranking).
  const shown = String(group.seasonId);
  const built = group.seasons.map(String);
  // So a drop onto (or from) this tile can tell a man from a woman (T2-16b) — her league's file
  // is one gender. Same value on every render, so writing it here is safe.
  if (group.kind === "file") rememberWomens(id, group.group.file.womens);
  return {
    seasonCodes: built.includes(shown) ? built : [shown, ...built],
    seasonCode: shown,
    defaultTemplate: group.kind === "file" ? herTemplate(group.group.file, id) : null,
    noFile: group.kind === "none" ? group.label : null,
    src: { kind: "live", group, playerId: id },
  };
}

function useSnapshotRadarValues(s: RadarSeason | null, template: string, axes: readonly RadarAxis[]): RadarValues {
  const ranges = useRadarRanges(template, axes);
  const population = useMemo(() => Object.values(readSnapshot().players) as RawPlayerRecord[], []);
  const data = s?.src.kind === "snapshot" ? s.src.data : null;
  return { values: data?.values ?? {}, hasData: data?.hasRadarMetrics ?? false, ranges, population };
}

function useLiveRadarValues(s: RadarSeason | null, template: string, axes: readonly RadarAxis[]): RadarValues {
  // Memoised on the file itself (a stable live answer), not step 1's fresh wrapper objects: a new
  // `ranges` map each render would restart the chart's update transition.
  const live = s?.src.kind === "live" ? s.src : null;
  const group = live?.group.kind === "file" ? live.group.group : null;
  const label = live?.group.kind === "file" ? live.group.league.label : null;
  const playerId = live?.playerId ?? null;
  return useMemo(() => {
    if (!group || label === null || playerId === null) {
      return { values: {}, hasData: false, ranges: liveRadarRanges([], template, axes), population: [] };
    }
    const population = templatePopulation(group, template, axes, label);
    const values = herValuesAt(group.file, playerId, template, axes);
    return { values: values ?? {}, hasData: values !== null, ranges: liveRadarRanges(population, template, axes), population };
  }, [group, label, playerId, template, axes]);
}

export const useRadarSeason: (name: string, id: PlayerId | null, season: string | undefined) => RadarSeason | null = LIVE
  ? useLiveRadarSeason
  : useSnapshotRadarSeason;

export const useRadarValues: (s: RadarSeason | null, template: string, axes: readonly RadarAxis[]) => RadarValues = LIVE
  ? useLiveRadarValues
  : useSnapshotRadarValues;

// --- a merged Radar (2–5 players) on ?live=1 (T2-16b) ------------------------------------

/** Step 1 — each player's group (her league, her tile's season at the merge), and the template a fresh
 *  comparison opens on: the first player's (the tile's own) position in her file. */
export interface LiveRadarGroups {
  groups: LiveGroupFile[];
  defaultTemplate: string | null;
}

/**
 * All players at once (`useLiveGroupFiles`). Notes each one's gender for the drop rules; the
 * rules only let in one gender, so two genders here is a bug (or a data problem) — thrown, never
 * drawn on one scale (as the Stats comparison).
 */
export function useLiveRadarGroups(
  ids: readonly PlayerId[],
  seasons: readonly (string | null | undefined)[] = [],
): LiveRadarGroups {
  const groups = useLiveGroupFiles(ids, seasons);
  const genders = new Set<boolean>();
  groups.forEach((g, i) => {
    if (g.kind !== "file") return;
    rememberWomens(ids[i]!, g.group.file.womens);
    genders.add(g.group.file.womens);
  });
  if (genders.size > 1) throw new Error(`Radar comparison: men and women on one tile (players ${ids.join(", ")})`);
  const first = groups[0];
  return {
    groups,
    defaultTemplate: first?.kind === "file" ? herTemplate(first.group.file, ids[0]!) : null,
  };
}

/** Step 2 — every player's values at the template, on ONE scale. */
export interface LiveRadarComparison {
  /** Per player, her values on the axes; {} when she has no file or no minutes at the template. */
  valuesList: Record<string, number | null>[];
  ranges: Map<string, RadarRange>;
  /** Who the scale is made from — the Distribution view's curves. */
  population: readonly RawPlayerRecord[];
}

/**
 * Each player's shape = her own league's file, her minutes at the template (Q-009), as the
 * one-player Radar; ONE scale = the first player's file (`radarComparison`, Q-010 / Q-011 B
 * meanwhile). A player from another league is drawn on that scale too (rules.md "Not following
 * the rules yet").
 */
export function useLiveRadarComparison(
  s: LiveRadarGroups,
  ids: readonly PlayerId[],
  template: string,
  axes: readonly RadarAxis[],
): LiveRadarComparison {
  const files = s.groups.map((g) => (g.kind === "file" ? g.group : null));
  const first = s.groups[0];
  const label = first?.kind === "file" ? first.league.label : null;
  // Memoised on who + which file (a widget's answer per file never changes; Retry remounts it),
  // not the fresh arrays: a new `ranges` map each render would restart the chart's transition.
  // A string, so adding a 3rd player doesn't change the deps' length.
  const key = ids.map((id, i) => `${id}@${files[i] ? `${files[i]!.file.competitionId}/${files[i]!.file.seasonId}` : "-"}`).join(",");
  return useMemo(() => radarComparison(files, ids, label, template, axes), [key, label, template, axes]);
}
